/**
 * ScrapeKing Design System v2 - Form Control Components
 * Checkbox, Radio, Switch, Slider
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC, InputHTMLAttributes, forwardRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const checkmark = keyframes`
  0% {
    stroke-dashoffset: 16;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(0, 200, 224, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(0, 200, 224, 0);
  }
`;

// ═══════════════════════════════════════════════════════════════
// CHECKBOX
// ═══════════════════════════════════════════════════════════════

const CheckboxWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-3);
  cursor: pointer;
  user-select: none;
`;

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const CheckboxBox = styled.span<{ $checked: boolean; $disabled?: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.$checked ? 'var(--sk-cyan-500)' : 'var(--sk-surface-border)'};
  border-radius: var(--sk-radius-sm);
  background: ${props => props.$checked ? 'var(--sk-cyan-600)' : 'var(--sk-bg-tertiary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;

  ${props => props.$disabled && css`
    opacity: 0.5;
    cursor: not-allowed;
  `}

  ${CheckboxWrapper}:hover & {
    border-color: ${props => !props.$disabled && (props.$checked ? 'var(--sk-cyan-400)' : 'var(--sk-surface-border-hover)')};
  }

  ${CheckboxInput}:focus-visible + & {
    box-shadow: 0 0 0 3px rgba(0, 200, 224, 0.2);
  }

  svg {
    width: 14px;
    height: 14px;
    color: var(--sk-text-primary);
    stroke-dasharray: 16;
    stroke-dashoffset: ${props => props.$checked ? '0' : '16'};
    transition: stroke-dashoffset 0.2s ease;
  }
`;

const CheckboxLabel = styled.span<{ $disabled?: boolean }>`
  font-size: var(--sk-text-sm);
  color: ${props => props.$disabled ? 'var(--sk-text-tertiary)' : 'var(--sk-text-primary)'};
`;

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({
  label,
  checked,
  disabled,
  className,
  ...props
}, ref) => {
  return (
    <CheckboxWrapper className={className}>
      <CheckboxInput
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        {...props}
      />
      <CheckboxBox $checked={!!checked} $disabled={disabled}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </CheckboxBox>
      {label && <CheckboxLabel $disabled={disabled}>{label}</CheckboxLabel>}
    </CheckboxWrapper>
  );
});

Checkbox.displayName = 'Checkbox';

// ═══════════════════════════════════════════════════════════════
// RADIO
// ═══════════════════════════════════════════════════════════════

const RadioWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-3);
  cursor: pointer;
  user-select: none;
`;

const RadioInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const RadioCircle = styled.span<{ $checked: boolean; $disabled?: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.$checked ? 'var(--sk-cyan-500)' : 'var(--sk-surface-border)'};
  border-radius: 50%;
  background: var(--sk-bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  ${props => props.$disabled && css`
    opacity: 0.5;
    cursor: not-allowed;
  `}

  ${RadioWrapper}:hover & {
    border-color: ${props => !props.$disabled && (props.$checked ? 'var(--sk-cyan-400)' : 'var(--sk-surface-border-hover)')};
  }

  ${RadioInput}:focus-visible + & {
    box-shadow: 0 0 0 3px rgba(0, 200, 224, 0.2);
  }

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--sk-cyan-500);
    transform: scale(${props => props.$checked ? 1 : 0});
    transition: transform 0.2s ease;
  }
`;

const RadioLabel = styled.span<{ $disabled?: boolean }>`
  font-size: var(--sk-text-sm);
  color: ${props => props.$disabled ? 'var(--sk-text-tertiary)' : 'var(--sk-text-primary)'};
`;

interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(({
  label,
  checked,
  disabled,
  className,
  ...props
}, ref) => {
  return (
    <RadioWrapper className={className}>
      <RadioInput
        ref={ref}
        type="radio"
        checked={checked}
        disabled={disabled}
        {...props}
      />
      <RadioCircle $checked={!!checked} $disabled={disabled} />
      {label && <RadioLabel $disabled={disabled}>{label}</RadioLabel>}
    </RadioWrapper>
  );
});

Radio.displayName = 'Radio';

// ═══════════════════════════════════════════════════════════════
// SWITCH / TOGGLE
// ═══════════════════════════════════════════════════════════════

const SwitchWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-3);
  cursor: pointer;
  user-select: none;
`;

const SwitchInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const SwitchTrack = styled.span<{ $checked: boolean; $disabled?: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: var(--sk-radius-full);
  background: ${props => props.$checked ? 'var(--sk-cyan-600)' : 'var(--sk-bg-tertiary)'};
  border: 1px solid ${props => props.$checked ? 'var(--sk-cyan-500)' : 'var(--sk-surface-border)'};
  position: relative;
  transition: all 0.2s ease;

  ${props => props.$disabled && css`
    opacity: 0.5;
    cursor: not-allowed;
  `}

  ${props => props.$checked && css`
    box-shadow: 0 0 15px var(--sk-cyan-glow);
  `}

  ${SwitchWrapper}:hover & {
    border-color: ${props => !props.$disabled && (props.$checked ? 'var(--sk-cyan-400)' : 'var(--sk-surface-border-hover)')};
  }

  ${SwitchInput}:focus-visible + & {
    box-shadow: 0 0 0 3px rgba(0, 200, 224, 0.2);
  }
`;

const SwitchThumb = styled.span<{ $checked: boolean }>`
  position: absolute;
  top: 2px;
  left: ${props => props.$checked ? 'calc(100% - 20px)' : '2px'};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--sk-text-primary);
  transition: left 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const SwitchLabel = styled.span<{ $disabled?: boolean }>`
  font-size: var(--sk-text-sm);
  color: ${props => props.$disabled ? 'var(--sk-text-tertiary)' : 'var(--sk-text-primary)'};
`;

interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(({
  label,
  checked,
  disabled,
  className,
  ...props
}, ref) => {
  return (
    <SwitchWrapper className={className}>
      <SwitchInput
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        {...props}
      />
      <SwitchTrack $checked={!!checked} $disabled={disabled}>
        <SwitchThumb $checked={!!checked} />
      </SwitchTrack>
      {label && <SwitchLabel $disabled={disabled}>{label}</SwitchLabel>}
    </SwitchWrapper>
  );
});

Switch.displayName = 'Switch';

// ═══════════════════════════════════════════════════════════════
// SLIDER
// ═══════════════════════════════════════════════════════════════

const SliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-2);
  width: 100%;
`;

const SliderLabel = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-xs);
  font-weight: 500;
  color: var(--sk-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SliderValue = styled.span`
  font-family: var(--sk-font-mono);
  color: var(--sk-cyan-500);
`;

const SliderInput = styled.input`
  width: 100%;
  height: 6px;
  border-radius: var(--sk-radius-full);
  background: var(--sk-bg-tertiary);
  outline: none;
  -webkit-appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--sk-cyan-500);
    border: 2px solid var(--sk-cyan-400);
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 0 10px var(--sk-cyan-glow);
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--sk-cyan-500);
    border: 2px solid var(--sk-cyan-400);
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  showValue?: boolean;
  valueFormatter?: (value: number) => string;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(({
  label,
  showValue = true,
  valueFormatter = (v) => v.toString(),
  value,
  className,
  ...props
}, ref) => {
  return (
    <SliderWrapper className={className}>
      {(label || showValue) && (
        <SliderLabel>
          <span>{label}</span>
          {showValue && <SliderValue>{valueFormatter(Number(value))}</SliderValue>}
        </SliderLabel>
      )}
      <SliderInput
        ref={ref}
        type="range"
        value={value}
        {...props}
      />
    </SliderWrapper>
  );
});

Slider.displayName = 'Slider';

// ═══════════════════════════════════════════════════════════════
// FORM FIELD WRAPPER
// ═══════════════════════════════════════════════════════════════

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-2);
`;

export const FormLabel = styled.label`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-xs);
  font-weight: 500;
  color: var(--sk-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const FormHelperText = styled.span<{ $error?: boolean }>`
  font-size: var(--sk-text-xs);
  color: ${props => props.$error ? 'var(--sk-red-400)' : 'var(--sk-text-tertiary)'};
`;

export default { Checkbox, Radio, Switch, Slider, FormField, FormLabel, FormHelperText };
